import React, { Component } from 'react'
import { withApollo } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import { Calendar, momentLocalizer, Navigate } from 'react-big-calendar'
import moment from 'moment'
import Color from 'color'
import { Button, DatePicker, Space, Row, Col } from 'antd'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Prompt, withRouter } from 'react-router-dom'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { v4 as uuidv4 } from 'uuid'
import { RRule, rrulestr } from 'rrule'
import { faEdit, faCopy, faSave } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'

import { Colors } from '../../components/Styles'
import EditScheduleEvent from './EditScheduleEventV3'
import { ProgramDetailsQuery, ProgramScheduleDevicesQuery } from './Queries'
import * as Utils from '../../components/Utils'
import BatchActions from './BatchActions'
import * as Auth from '../../auth'
import { openCustomConfirmModal } from '../../components/CustomConfirmModal'
import ScheduleTimezoneSelect, {
	LOCAL_TIMEZONE,
	deviceTimezoneOptions,
	initialTimezoneKey,
	nowGetter,
	persistTimezoneKey
} from '../../components/ScheduleTimezoneSelect'

const localizer = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(Calendar)

// the week containing the given date, as displayed by the calendar
const weekRange = (date) => ({
	start: moment(date)
		.weekday(0)
		.hour(0)
		.minute(0)
		.second(0)
		.subtract(1, 'second')
		.toDate(),
	end: moment(date)
		.weekday(6)
		.add(1, 'days')
		.hour(0)
		.minute(0)
		.second(0)
		.toDate()
})

const CustomCalendarToolbar = ({
	date,
	onNavigate,
	timezoneSelector,
	actions
}) => {
	return (
		// the datepicker keeps its width and the actions take the rest,
		// the gutter separates them when they wrap on small screens
		<Row style={{ marginTop: 10, marginBottom: 10 }} gutter={[0, 8]}>
			<Col flex="none">
				<Button.Group style={{ margin: 0 }}>
					<Button onClick={() => onNavigate(Navigate.PREVIOUS)}>
						&lt;&lt;
					</Button>
				</Button.Group>

				<DatePicker
					showTime={false}
					picker="week"
					format="DD-MM-YYYY"
					value={moment(date)}
					onChange={(v) => {
						if (v) {
							onNavigate(Navigate.DATE, v.toDate())
						}
					}}
					renderExtraFooter={() => (
						<div
							style={{ textAlign: 'center', cursor: 'pointer' }}
							onClick={() => onNavigate(Navigate.TODAY)}
						>
							<a>This week</a>
						</div>
					)}
				/>

				<Button.Group style={{ margin: 0 }}>
					<Button onClick={() => onNavigate(Navigate.NEXT)}>
						&gt;&gt;
					</Button>
				</Button.Group>
			</Col>

			<Col
				flex="auto"
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center'
				}}
			>
				<Space wrap>
					{timezoneSelector}
					{actions}
				</Space>
			</Col>
		</Row>
	)
}

export const formats = {
	dayFormat: (date, culture, localizer) =>
		localizer.format(date, 'ddd D', culture),

	timeGutterFormat: (time, culture, localizer) =>
		localizer.format(time, 'HH:mm', culture),

	eventTimeRangeFormat: ({ start, end }, culture, localizer) => {
		if (end.getSeconds() === 59) {
			end.setSeconds(60)
		}
		return `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`
	}
}

class SchedulerV3 extends Component {
	state = {
		calendarEvents: null,
		events: {},
		editing: null,
		height: null,
		start: null,
		end: null,
		colors: {},
		editable: false,
		batchActionsModal: false,
		dirty: false,
		// id of an event created by a slot selection but not yet confirmed
		// in the edit dialog, so it can be discarded on cancel
		creating: null,
		saving: false,
		// the calendar date, controlled to open on the week of the selected timezone
		date: new Date(),
		// devices (and the local time) whose timezone can be used for the current time
		timezoneOptions: [],
		timezoneKey: LOCAL_TIMEZONE,
		getNow: () => new Date()
	}

	componentDidMount() {
		this.createEvents(this.props)
		this.loadTimezoneOptions(this.props.program._id)
		window.addEventListener('beforeunload', this.onBeforeUnload)
	}

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.onBeforeUnload)
	}

	onBeforeUnload = (e) => {
		if (this.state.dirty) {
			e.preventDefault()
			e.returnValue = ''
		}
	}

	componentWillReceiveProps(props) {
		if (props.program && this.props.program._id !== props.program._id) {
			this.setState({ dirty: false, editable: false, creating: null })
			this.createEvents(props)
			this.loadTimezoneOptions(props.program._id)
		} else if (
			props.program &&
			this.props.program.updatedBy?.date !==
				props.program.updatedBy?.date &&
			!this.state.dirty
		) {
			this.createEvents(props)
		}
	}

	createEvents = (props) => {
		let events = {}
		let colors = {}
		if (props.program.playlists) {
			for (let i in props.program.playlists) {
				const pl = props.program.playlists[i]
				colors[pl._id] = Colors.playlistColors[i]
				if (pl.scheduleV3) {
					for (let evt of pl.scheduleV3) {
						if (evt.rule) {
							const id = uuidv4()
							events[id] = {
								...evt,
								id,
								playlist: pl
							}
						}
					}
				}
			}
		}

		const { start, end } =
			this.state.start && this.state.end ?
				this.state
			:	weekRange(this.getNow())

		let calendarEvents = this.createCalendarEvents(events, start, end)

		this.setState({ events, calendarEvents, start, end, colors })
	}

	// the program devices with a known timezone, sorted by name
	loadTimezoneOptions = async (programId) => {
		let options = []
		try {
			const res = await this.props.client.query({
				query: ProgramScheduleDevicesQuery,
				variables: { id: programId }
			})
			const program = res.data?.programById
			options = deviceTimezoneOptions([
				...(program?.zones || []).map((z) => z.device),
				...(program?.thirdPartyDevices || [])
			])
		} catch (e) {
			// fall back to the local time
			console.error(e)
		}

		// the program changed while loading
		if (this.props.program._id !== programId) {
			return
		}

		const timezoneKey = initialTimezoneKey(
			options,
			this.props.location.search
		)
		this.setState(
			{
				timezoneOptions: options,
				timezoneKey,
				getNow: nowGetter(options, timezoneKey)
			},
			() => {
				// open the calendar on the current week of the selected timezone
				const date = this.getNow()
				const { start, end } = weekRange(date)
				this.setState({
					date,
					start,
					end,
					calendarEvents: this.createCalendarEvents(
						this.state.events,
						start,
						end
					)
				})
			}
		)
	}

	selectTimezone = (timezoneKey) => {
		this.setState((state) => ({
			timezoneKey,
			getNow: nowGetter(state.timezoneOptions, timezoneKey)
		}))
		persistTimezoneKey(timezoneKey)
	}

	getNow = () => this.state.getNow()

	renderTimezoneSelector = () => (
		<ScheduleTimezoneSelect
			options={this.state.timezoneOptions}
			value={this.state.timezoneKey}
			onChange={this.selectTimezone}
		/>
	)

	setRange = (dates) => {
		if (dates.length > 0) {
			const start = moment(dates[0]).subtract(1, 'second').toDate()
			const end = moment(dates[dates.length - 1])
				.add(1, 'days')
				.toDate()
			const calendarEvents = this.createCalendarEvents(
				this.state.events,
				start,
				end
			)

			this.setState({
				start,
				end,
				calendarEvents
			})
		}
	}

	createCalendarEvents = (
		events,
		from = this.state.start,
		until = this.state.end
	) => {
		let calendarEvents = []
		for (let id in events) {
			const evt = events[id]
			const eventRule = rrulestr(evt.rule)
			const localFrom = Utils.localToUtcDate(from)
			const localUntil = Utils.localToUtcDate(until)

			const eventTimes = eventRule.between(
				localFrom.toDate(),
				localUntil.toDate()
			)

			// The times we're getting from rrule are UTC times but the browser converts them to local times
			// What we want are local times with the same hour as the UTC time
			// So we subtract the local tz offset from the UTC time
			const eventDayEvents = eventTimes.map((e) => {
				let localTime = Utils.utcToLocalDate(e)
				let localEndTime = moment(localTime).add(
					evt.duration,
					'minutes'
				)

				// passage à l'heure d'été/hiver pendant la plage horaire
				if (localTime.isDST() !== localEndTime.isDST()) {
					console.log(
						`DST shifts during event [${localTime.isDST()} => ${localEndTime.isDST()}]`
					)
					localEndTime.add(localTime.isDST() ? 1 : -1, 'hour')
				}

				return {
					id: evt.id,
					start: localTime.toDate(),
					end: localEndTime.toDate()
				}
			})

			calendarEvents = [...calendarEvents, ...eventDayEvents]
		}
		return calendarEvents
	}

	onEventDrop = (drop) => {
		if (this.state.editable && drop.event.id) {
			const event = this.state.events[drop.event.id]

			if (event) {
				// update rule with start date
				let oldRule = rrulestr(event.rule)
				let localTime = Utils.localToUtcDate(drop.start)
				let oldRuleSet

				let duration = Utils.duration(drop.start, drop.end)

				// for rule sets we need to get the internal rule
				if (oldRule._exdate) {
					oldRuleSet = oldRule
					oldRule = oldRule._rrule[0]
				}

				const oldStartDate = moment(oldRule.options.dtstart)

				const editRule = () => {
					let newRule = new RRule({
						dtstart: localTime.toDate(),
						freq: oldRule.options.freq,
						until: oldRule.options.until,
						interval: oldRule.options.interval,
						count: oldRule.options.count,
						byweekday: oldRule.options.byweekday,
						bymonthday: oldRule.options.bymonthday
					})

					// If old rule was a set recreate it
					if (oldRuleSet) {
						newRule = Utils.updatedRuleSetWithRule(
							oldRuleSet,
							newRule
						)
					}

					this.setEvent(
						drop.event.id,
						newRule.toString(),
						duration,
						event.weight,
						event.playlist
					)
				}

				// For reccuring events, ask change occurence only or all
				if (oldRule.options.count !== 1) {
					const { intl } = this.props
					openCustomConfirmModal({
						title: intl.formatMessage({
							id: 'messagePrograms.details.edit.confirm.title'
						}),
						content: intl.formatMessage(
							{
								id: 'messagePrograms.details.edit.confirm.message'
							},
							{
								date: intl.formatDate(drop.event.start, {
									day: 'numeric',
									month: 'long',
									year: 'numeric',
									hour: '2-digit',
									minute: '2-digit'
								})
							}
						),
						primaryActionText: intl.formatMessage({
							id: 'messagePrograms.details.confirm.all'
						}),
						secondaryActionText: intl.formatMessage({
							id: 'messagePrograms.details.confirm.instanceOnly'
						}),
						cancelText: intl.formatMessage({
							id: 'actions.cancel'
						}),
						onPrimaryAction: () => {
							// Edit all occurences
							// Keep start date on the same day but change time
							localTime = Utils.localToUtcDate(
								drop.start,
								oldStartDate.toDate()
							) // The offset needs to be calculated based on the old date
							localTime.date(oldStartDate.date())
							localTime.month(oldStartDate.month())
							localTime.year(oldStartDate.year())

							editRule()
						},
						onSecondaryAction: () => {
							// Edit single occurence
							// Delete specific event and create a new identical one at the dropped time

							let events = { ...this.state.events }

							// Exclude drop date from event
							let rruleSet = rrulestr(event.rule, {
								forceset: true
							})
							rruleSet.exdate(
								Utils.localToUtcDate(drop.event.start).toDate()
							)
							events[event.id].rule = rruleSet.toString()

							// Create new single event at drop date
							let newEvent = {
								...event,
								duration,
								id: uuidv4(),
								rule: new RRule({
									dtstart: Utils.localToUtcDate(
										drop.start
									).toDate(),
									count: 1
								}).toString()
							}
							events[newEvent.id] = newEvent
							this.applyEvents(events)
						}
					})
				} else {
					editRule()
				}
			}
		}
	}

	setEvent = (id, rule = null, duration, weight, playlist = null) => {
		if (this.state.editable) {
			let events = { ...this.state.events }
			if (rule || playlist) {
				let event = this.state.events[id]

				if (event) {
					if (rule) {
						event.rule = rule
					}
					if (playlist) {
						event.playlist = playlist
					}
					if (duration) {
						event.duration = duration
					}
					if (weight) {
						event.weight = weight
					}

					events[id] = event
				}
			} else {
				delete events[id]
			}
			this.applyEvents(events)
		}
	}

	// Buffer the modifications in memory, they are only sent
	// to the backend when the user saves
	applyEvents = (events) => {
		const calendarEvents = this.createCalendarEvents(events)
		this.setState({ events, calendarEvents, dirty: true })
	}

	save = async () => {
		this.setState({ saving: true })
		try {
			let schedules = {}
			for (let pl of this.props.program.playlists) {
				schedules[pl._id] = []
			}

			for (let evt in this.state.events) {
				let event = this.state.events[evt]
				if (event.playlist && schedules[event.playlist._id]) {
					schedules[event.playlist._id].push({
						rule: event.rule,
						duration: event.duration,
						weight: event.weight
					})
				}
			}

			const res = await this.props.client.mutate({
				variables: {
					schedule: JSON.stringify(schedules),
					programId: this.props.program._id
				},
				mutation: gql`
					mutation updateProgramPlaylistScheduleV3(
						$schedule: String!
						$programId: String!
					) {
						programPlaylistScheduleV3Update(
							schedule: $schedule
							programId: $programId
						) {
							success
							message
						}
					}
				`,
				refetchQueries: [
					{
						query: ProgramDetailsQuery,
						variables: { id: this.props.program._id }
					}
				]
			})
			if (res.data?.programPlaylistScheduleV3Update?.success !== true) {
				console.error(
					`The event couldn't have been created: ${res.data.programPlaylistScheduleV3Update.message}`
				)
				throw new Error(`EVENT_NOT_SAVED`)
			}
			this.setState({ dirty: false, editable: false, creating: null })
		} catch (e) {
			Utils.displayError(e)
		} finally {
			this.setState({ saving: false })
		}
	}

	cancel = () => {
		this.setState(
			{
				dirty: false,
				editable: false,
				editing: null,
				creating: null,
				batchActionsModal: false
			},
			() => this.createEvents(this.props)
		)
	}

	handleSelect = (calEvent) => {
		if (this.state.editable) {
			this.setState((state) => {
				let event
				let creating = null
				let events = { ...state.events }
				if (calEvent.id) {
					event = this.state.events[calEvent.id]
				} else {
					// The dialog subtracts the offset so that the UTC date from events show in local time
					// But calEvent.start is already in local time, so we compensate here
					let localTime = Utils.localToUtcDate(calEvent.start)
					event = {
						id: uuidv4(),
						weight: 3,
						duration: Utils.duration(calEvent.start, calEvent.end),
						playlist:
							(
								this.props.playlists &&
								this.props.playlists.length > 0
							) ?
								this.props.playlists[0]
							:	null,
						rule: new RRule({
							dtstart: localTime.toDate(),
							freq: RRule.WEEKLY,
							interval: 1
						}).toString()
					}
					// the event goes in the buffer right away so the dialog can
					// edit it, mark it as pending confirmation
					events[event.id] = event
					creating = event.id
				}
				event.start = calEvent.start

				return {
					editing: event,
					events,
					creating
				}
			})
		}
	}

	titleAccessor = (calEvent) => {
		const event = this.state.events[calEvent.id]
		if (event) {
			return `${event.playlist ? event.playlist.name : this.props.intl.formatMessage({ id: 'messagePrograms.details.noFileSelected' })} [${event.weight}]`
		}
		return ''
	}

	renderActions = () => {
		const canEditSchedule =
			Auth.hasAccess('program:edit', this.props.program.teamId) &&
			Auth.hasPermission('programPlaylist:edit:setSchedule') &&
			(Auth.isAdmin() || Auth.cache.team?.allowProgramEditing)

		if (!canEditSchedule) {
			return null
		}

		if (!this.state.editable) {
			return (
				<Button
					type="primary"
					dataTest="program-schedule-edit-button"
					onClick={() => this.setState({ editable: true })}
				>
					<FontAwesomeIcon icon={faEdit} />
					&nbsp;
					<FormattedMessage id="actions.edit" />
				</Button>
			)
		}

		return (
			<Space>
				<Button
					onClick={() => this.setState({ batchActionsModal: true })}
				>
					<FontAwesomeIcon icon={faCopy} />
					&nbsp;
					<FormattedMessage id="programs.scheduler.batch" />
				</Button>
				<Button
					type="primary"
					dataTest="program-schedule-save-button"
					disabled={!this.state.dirty}
					loading={this.state.saving}
					onClick={this.save}
				>
					<FontAwesomeIcon icon={faSave} />
					&nbsp;
					<FormattedMessage id="actions.save" />
				</Button>
				<Button
					dataTest="program-schedule-cancel-button"
					disabled={this.state.saving}
					onClick={this.cancel}
				>
					<FormattedMessage id="actions.cancel" />
				</Button>
			</Space>
		)
	}

	// stable wrapper identity: an inline arrow in render() would remount the
	// toolbar on every render and close the datepicker popover
	renderToolbar = (toolbarProps) => (
		<CustomCalendarToolbar
			{...toolbarProps}
			timezoneSelector={
				!this.state.editable && this.renderTimezoneSelector()
			}
			actions={this.renderActions()}
		/>
	)

	calendarComponents = { toolbar: this.renderToolbar }

	render() {
		const { program } = this.props

		return (
			<React.Fragment>
				<div
					className={`noAllDay${this.state.editable ? '' : ' noResize'}`}
					style={{ marginBottom: 20 }}
				>
					<DnDCalendar
						localizer={localizer}
						events={this.state.calendarEvents || []}
						formats={formats}
						selectable={this.state.editable}
						style={{ height: this.state.height }}
						views={['week']}
						defaultView="week"
						drilldownView="week"
						step={5}
						timeslots={12}
						date={this.state.date}
						onNavigate={(date) => this.setState({ date })}
						getNow={this.state.getNow}
						showMultiDayTimes={true}
						titleAccessor={this.titleAccessor}
						tooltipAccessor={this.titleAccessor}
						draggableAccessor={() => this.state.editable}
						resizableAccessor={() => this.state.editable}
						onEventDrop={this.onEventDrop}
						onEventResize={this.onEventDrop}
						endAccessor={(e) => {
							// make events that end at midnight a second shorter so they dont carry over
							const end = moment(e.end)
							if (end.hour() === 0 && end.minute() === 0) {
								return end.subtract(1, 'second').toDate()
							}
							return e.end
						}}
						onSelectSlot={this.handleSelect}
						onSelectEvent={this.handleSelect}
						onRangeChange={this.setRange}
						onView={(e) =>
							this.setState({
								height: e === 'month' ? 600 : null
							})
						}
						eventPropGetter={(calEvent) => {
							const event = this.state.events[calEvent.id]
							if (event && event.playlist) {
								const c = this.state.colors[event.playlist._id]
								return {
									style: {
										backgroundColor: Color(c).fade(0.5),
										border: `1px solid ${c}`,
										color: '#000',
										fontSize: 10
									}
								}
							}
							return null
						}}
						components={this.calendarComponents}
					/>
					{this.state.editing && (
						<EditScheduleEvent
							event={this.state.editing}
							playlists={this.props.program.playlists}
							onClose={(id, rule, duration, weight, playlist) => {
								if (id) {
									this.setState({
										editing: false,
										creating: null
									})
									this.setEvent(
										id,
										rule,
										duration,
										weight,
										playlist
									)
								} else {
									// the dialog was cancelled, discard the event
									// created on slot selection if there is one
									this.setState((state) => {
										let events = { ...state.events }
										if (state.creating) {
											delete events[state.creating]
										}
										return {
											editing: false,
											creating: null,
											events
										}
									})
								}
							}}
						/>
					)}
					{this.state.batchActionsModal && (
						<BatchActions
							events={this.state.events}
							playlists={this.props.program.playlists}
							setEvents={(evt) => this.applyEvents(evt)}
							onClose={() =>
								this.setState({ batchActionsModal: false })
							}
						/>
					)}
				</div>
				<Prompt
					when={this.state.dirty}
					message={JSON.stringify({
						title: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.title'
						}),
						message: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.message'
						}),
						leaveText: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.leave'
						}),
						stayText: this.props.intl.formatMessage({
							id: 'scheduler.unsavedChanges.stay'
						})
					})}
				/>
			</React.Fragment>
		)
	}
}

export default withRouter(withApollo(injectIntl(SchedulerV3)))
